/**
 * Phase 6 cross-cutting PR 2 — `<NotifDropdown>`.
 *
 * Popover that lists the notifications. At Default+ this is a
 * 380px-wide popover anchored above the bell trigger (the SideNav
 * footer is bottom-of-rail, so the dropdown opens upward). At Mobile
 * tier the popover becomes a full-width bottom-sheet (deferred:
 * Mobile chrome ships with PR 5 polish; this PR is Default+ only).
 *
 * **Mark-all-read** appears in the header only when there's at least
 * one unread row. Click fires the optimistic mutation; the dropdown
 * stays open and re-renders without the button.
 *
 * **Click outside / Esc** closes the popover. The click-outside
 * detection arms with a 100ms delay to prevent the same click that
 * opened the popover from immediately closing it.
 */

import { useEffect, useRef, type RefObject } from 'react';
import { NotifRow } from './NotifRow';
import { type Notification } from '../../hooks/useNotifications';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './NotifDropdown.module.css';

interface NotifDropdownProps {
  notifications: Notification[];
  unreadCount: number;
  onMarkAsRead: (id: string) => Promise<void>;
  onMarkAllAsRead: () => Promise<void>;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement>;
  /**
   * PR 2b — passed through to each NotifRow. When an `ai_escalation`
   * row is clicked, this fires (and the row does NOT navigate) so the
   * parent BellTrigger can open the in-place EscalationSummaryModal.
   */
  onEscalationClick?: (notification: Notification) => void;
}

export function NotifDropdown({
  notifications,
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  onClose,
  anchorRef,
  onEscalationClick,
}: NotifDropdownProps): JSX.Element {
  const iconsOn = useIconSystem();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Click-outside + Esc handlers. 100ms arm delay prevents the open-click
  // from immediately closing the dropdown (the same event would bubble
  // to document otherwise).
  useEffect(() => {
    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, 100);

    const handleOutsideClick = (e: MouseEvent): void => {
      if (!armed) return;
      const target = e.target as Node;
      if (dropdownRef.current?.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      onClose();
    };
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEsc);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [anchorRef, onClose]);

  const hasUnread = unreadCount > 0;
  const isEmpty = notifications.length === 0;

  return (
    <div
      ref={dropdownRef}
      className={styles.dropdown}
      role="dialog"
      aria-label="Notifications"
    >
      <div className={styles.header}>
        <h4 className={styles.title}>Notifications</h4>
        {hasUnread ? (
          <button type="button" className={styles.markAll} onClick={() => onMarkAllAsRead()}>
            Mark all read
          </button>
        ) : null}
      </div>
      <div className={styles.list}>
        {isEmpty ? (
          <div className={styles.empty}>
            {/* A2 — `spot.empty-notify` @48 ("All quiet"). Spot scale:
                48 grid at stroke 2. */}
            <span className={styles.emptyGlyph} aria-hidden="true">
              {iconsOn ? <Icon name="spot.empty-notify" size={48} /> : '🔔'}
            </span>
            <p className={styles.emptyTitle}>Caught up</p>
            <p className={styles.emptySub}>We'll ping you when something happens.</p>
          </div>
        ) : (
          notifications.map((n) => (
            <NotifRow
              key={n.id}
              notification={n}
              onActivate={(id) => {
                void onMarkAsRead(id);
                onClose();
              }}
              onEscalationClick={onEscalationClick}
            />
          ))
        )}
      </div>
    </div>
  );
}
